/**
 * The URL chooses the tool and the default format (IMAGE_EDITOR_V2.md R4/R5): `crop-jpg` opens on
 * Crop saving as JPG; `/image-resizer` opens on Resize saving as the original format. One table,
 * shared by all 69 per-format slugs plus the generic ones. Pure; tested in routing.spec.ts.
 */
import type { Tool } from './model/edit-stack';
import type { ImageFormat } from './codecs/types';

export interface LandingSpec {
    slug: string;
    tool: Tool;
    /** 'original' = "Same as original" (R5); otherwise the page's format. */
    defaultFormat: ImageFormat | 'original';
    /** The format the page is about, for copy ("Crop a JPG"), or null for generic pages. */
    pageFormat: ImageFormat | null;
    /** Human name used in the empty state and the button. */
    formatLabel: string | null;
    /** True only for /bulk-image-resizer: the multi-file drop zone leads (PLAN C2). Whether the
     *  page actually shows it is the flag's call (bulk-gate.ts landingCopy). */
    batchFirst?: boolean;
}

const TOOL_BY_PREFIX: Record<string, Tool> = { resize: 'resize', crop: 'crop', rotate: 'rotate', flip: 'flip' };

/** Extension token in a slug → editor format. Tokens the editor cannot save to still map to a format
 *  (heic, apng) so the copy can name them; the codec fallback decides what is actually written. */
const FORMAT_BY_TOKEN: Record<string, ImageFormat> = {
    jpg: 'jpg', jpeg: 'jpg', jfif: 'jpg', png: 'png', apng: 'apng', webp: 'webp', gif: 'gif', bmp: 'bmp',
    tiff: 'tiff', tif: 'tiff', ico: 'ico', avif: 'avif', heic: 'heic', heif: 'heic', psd: 'psd', ppm: 'ppm', eps: 'eps',
};

const LABEL_BY_FORMAT: Record<ImageFormat, string> = {
    jpg: 'JPG', png: 'PNG', webp: 'WebP', gif: 'GIF', apng: 'APNG', bmp: 'BMP', tiff: 'TIFF', ico: 'ICO', avif: 'AVIF',
    heic: 'HEIC', psd: 'PSD', ppm: 'PPM', eps: 'EPS', raw: 'RAW', unknown: 'image',
};

const PAGE_LABEL_BY_TOKEN: Record<string, string> = { heif: 'HEIF', jfif: 'JFIF' };

export function landingFor(slugOrPath: string | undefined | null): LandingSpec {
    const slug = (slugOrPath || '').replace(/^\/+/, '').split(/[?#]/)[0].split('/')[0].toLowerCase();
    if (slug === 'bulk-image-resizer') return { slug, tool: 'resize', defaultFormat: 'original', pageFormat: null, formatLabel: null, batchFirst: true };
    if (slug === 'image-resizer' || slug === '') return { slug: slug || 'image-resizer', tool: 'resize', defaultFormat: 'original', pageFormat: null, formatLabel: null };
    const m = /^(resize|crop|rotate|flip)-([a-z0-9]+)$/.exec(slug);
    if (!m) return { slug, tool: 'resize', defaultFormat: 'original', pageFormat: null, formatLabel: null };
    const tool = TOOL_BY_PREFIX[m[1]];
    const token = m[2];
    if (token === 'image' || token === 'images' || token === 'photo' || token === 'photos') {
        return { slug, tool, defaultFormat: 'original', pageFormat: null, formatLabel: null };
    }
    const format = FORMAT_BY_TOKEN[token];
    if (!format) return { slug, tool, defaultFormat: 'original', pageFormat: null, formatLabel: null };
    // HEIF pages say HEIF (regression-7, visual-14), JFIF pages say JFIF (J6-1, J7-7): the codec is
    // HEIC's / JPG's, the name is the page's
    return { slug, tool, defaultFormat: format, pageFormat: format, formatLabel: PAGE_LABEL_BY_TOKEN[token] || LABEL_BY_FORMAT[format] };
}

export function formatLabel(format: ImageFormat): string { return LABEL_BY_FORMAT[format] ?? format.toUpperCase(); }

/** Copy for the empty state, derived from the landing. Titles/H1 still come from page data. */
export function emptyStateCopy(l: LandingSpec): { verb: string; noun: string; button: string } {
    const verbs: Record<Tool, string> = { resize: 'Resize', crop: 'Crop', rotate: 'Rotate', flip: 'Flip' };
    const noun = l.formatLabel ? `a ${l.formatLabel}` : 'an image';
    // EPS / RAW pages (recheck 2026-10-08, P3): with imageEditorBulk off (prod) the editor skips those files, so
    // "Choose EPS files" promised what the picker refuses; a neutral label is true in both flag states (no CLS swap).
    const serverOnly = l.pageFormat === 'eps' || l.pageFormat === 'raw';
    return { verb: verbs[l.tool], noun, button: l.formatLabel && !serverOnly ? `Choose ${l.formatLabel} files` : serverOnly ? 'Choose files' : 'Choose photos' };
}

/**
 * The empty state's one formats line, identical on every editor slug whatever featureFlags.imageEditorBulk
 * says (QA round 2 redesign, 2026-10-05): the flag lands ~1 s after first paint, so a flag-dependent line
 * moved the page (CLS). It must be true in BOTH flag states (ruling 4, J1-8 / J4-6): with the flag off
 * nothing is uploaded, so it makes no server claim and names no format as browser-only. Where a file is
 * processed is said only after it is chosen (skippedMessage, the editor's own preparing states).
 */
export const EMPTY_FORMATS_LINE = 'JPG, PNG, WebP, GIF, BMP, HEIC, TIFF and more.';
